<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom right">
    <template #activator="{ props }">
      <slot :props="props"></slot>
    </template>
    <v-card>
      <v-date-picker
        v-model="internalDates"
        color="primary"
        hide-header
        multiple="range"
      ></v-date-picker>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="
            dates = [];
            menu = false;
          "
          >{{ t("common.clear") }}</v-btn
        >
        <v-btn @click="apply()">{{ t("common.apply") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { ref, watch } from "vue";

const { t } = useI18n();
const dates = defineModel<string[]>({
  required: true,
});

const internalDates = ref<string[]>([]);

const formatDate = (value: string | Date) => Intl.DateTimeFormat("fr-CA").format(new Date(value));

const toRangeEndpoints = (values: string[]) => {
  const first = values[0];
  const last = values[values.length - 1];
  if (values.length <= 1 || !first || !last) {
    return values.map(formatDate);
  }
  return [formatDate(first), formatDate(last)];
};

/**
 * Vuetify 4's multiple="range" only emits the start and end dates.
 * Expand to every day in the interval so consumers can keep using includes()-style filters.
 */
const expandRange = (values: string[]) => {
  const first = values[0];
  const last = values[values.length - 1];
  if (values.length <= 1 || !first || !last) {
    return values.map(formatDate);
  }

  const start = new Date(first);
  const end = new Date(last);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return values.map(formatDate);
  }

  const [rangeStart, rangeEnd] = start <= end ? [start, end] : [end, start];
  const expanded: string[] = [];
  const cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    expanded.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return expanded;
};

watch(dates, (value) => {
  internalDates.value = toRangeEndpoints(value);
});

const apply = () => {
  dates.value = expandRange(internalDates.value);
  menu.value = false;
};

const menu = ref(false);

watch(menu, (value) => {
  if (!value) {
    internalDates.value = toRangeEndpoints(dates.value);
  }
});
</script>
